// AI Elements `message` — adapted from the upstream shadcn registry
// template (sprint ai-elements-rendering). Deviations from upstream:
// - MessageBranch* removed: branch navigation is a D-008 non-goal (the
//   composer is the resume; no alternate-response affordances).
// - Streamdown → our MarkdownRenderer (DocPathChip interleaving, D-012).
// - `ai` SDK types → local ./types (type-only usage; dep not justified).
// - User-visible strings routed through i18n (spec §6.6).
// - Button size "icon-sm" → "icon" (our ui/button has no icon-sm and
//   ui/ components are out of scope per spec §4).

// DS.8 (D9/D2) — `ui/button.tsx` and `ui/tooltip.tsx` retire here. The
// successors are the DS pieces through the DS.5 adapters: `ds/DsIconButton`
// (`@ds/actions/IconButton` with its Material ripple suppressed) and
// `ds/DsTooltip` (`@ds/feedback/Tooltip`, portalled into `.aos-ds-root`).
//
// D3 makes this swap CONDITIONAL on byte-identity because the
// `user-bubble-hover` baseline is a founder-approved pixel contract (D-036)
// — and the condition is MET for a reason worth writing down: every symbol
// below that touched `Button`/`Tooltip` (`MessageActions`, `MessageAction`,
// `MessageAttachment`, `MessageAttachments`, `MessageToolbar`) has ZERO
// consumers [medido: grep -rn 'MessageAction|MessageAttachment' over web/src
// minus this file -> 0 hits, 2026-09-14; the only two importers of this
// module, organisms/{UserMessage,AssistantMessage}.tsx, take `Message`,
// `MessageContent` and `MessageResponse` only]. The Edit/Resend icons the
// hover baseline captures are `organisms/UserMessage.tsx`'s own, not these.
// So no in-frame PNG can move, and D3's fallback branch is not needed.
// The exports STAY: this is D-026 vendored surface (section 5 — new timeline
// features MUST extend these primitives), not dead code to delete.
import { DsIconButton } from '../ds/DsIconButton';
import { DsTooltip } from '../ds/DsTooltip';
import { cn } from '@/lib/utils';
import { PaperclipIcon, XIcon } from 'lucide-react';
import type { ComponentProps, HTMLAttributes } from 'react';
import { memo } from 'react';
import { useTranslation } from '../../i18n';
import { MarkdownRenderer } from '../MarkdownRenderer';
import type { AttachmentPart, MessageRole } from './types';

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: MessageRole;
};

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      'group flex w-full max-w-[95%] flex-col gap-2',
      from === 'user' ? 'is-user ml-auto justify-end' : 'is-assistant',
      className
    )}
    {...props}
  />
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({
  children,
  className,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(
      'flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden text-sm',
      'group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground',
      'group-[.is-assistant]:text-foreground',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export type MessageActionsProps = ComponentProps<'div'>;

export const MessageActions = ({
  className,
  children,
  ...props
}: MessageActionsProps) => (
  <div className={cn('flex items-center gap-1', className)} {...props}>
    {children}
  </div>
);

export type MessageActionProps = Omit<
  ComponentProps<typeof DsIconButton>,
  'aria-label'
> & {
  tooltip?: string;
  label?: string;
};

export const MessageAction = ({
  tooltip,
  children,
  label,
  variant = 'standard',
  size = 'sm',
  ...props
}: MessageActionProps) => {
  // `aria-label` is REQUIRED by `@ds/actions/IconButton` (its props declare it
  // non-optional), so the label/tooltip pair that used to reach the user only
  // through an `.sr-only` span is now the button's own accessible name — a
  // strictly better outcome, and the reason the span is gone rather than
  // duplicated into a second announcement.
  const button = (
    <DsIconButton
      aria-label={label || tooltip || ''}
      size={size}
      type="button"
      variant={variant}
      {...props}
    >
      {children}
    </DsIconButton>
  );

  if (tooltip) {
    return <DsTooltip content={tooltip}>{button}</DsTooltip>;
  }

  return button;
};

export type MessageResponseProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  children: string;
  onDocPathClick?: (path: string) => void;
};

export const MessageResponse = memo(
  ({ className, children, onDocPathClick, ...props }: MessageResponseProps) => (
    <div
      className={cn(
        'size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className
      )}
      {...props}
    >
      <MarkdownRenderer text={children} onDocPathClick={onDocPathClick} />
    </div>
  ),
  (prevProps, nextProps) => prevProps.children === nextProps.children
);

MessageResponse.displayName = 'MessageResponse';

export type MessageAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: AttachmentPart;
  className?: string;
  onRemove?: () => void;
};

export function MessageAttachment({
  data,
  className,
  onRemove,
  ...props
}: MessageAttachmentProps) {
  const { t } = useTranslation();
  const filename = data.filename || '';
  const mediaType =
    data.mediaType?.startsWith('image/') && data.url ? 'image' : 'file';
  const isImage = mediaType === 'image';
  const attachmentLabel =
    filename ||
    (isImage ? t('aiElements.attachments.image') : t('aiElements.attachments.file'));

  return (
    <div
      className={cn(
        'group relative size-24 overflow-hidden rounded-lg',
        className
      )}
      {...props}
    >
      {isImage ? (
        <>
          <img
            alt={attachmentLabel}
            className="size-full object-cover"
            height={100}
            src={data.url}
            width={100}
          />
          {onRemove && (
            <DsIconButton
              aria-label={t('aiElements.attachments.remove')}
              className="absolute top-2 right-2 size-6 rounded-full bg-background/80 p-0 opacity-0 backdrop-blur-sm transition-opacity hover:bg-background group-hover:opacity-100 [&>svg]:size-3"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              size="sm"
              type="button"
              variant="standard"
            >
              <XIcon />
            </DsIconButton>
          )}
        </>
      ) : (
        <>
          <DsTooltip content={attachmentLabel}>
            <div className="flex size-full shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <PaperclipIcon className="size-4" />
            </div>
          </DsTooltip>
          {onRemove && (
            <DsIconButton
              aria-label={t('aiElements.attachments.remove')}
              className="size-6 shrink-0 rounded-full p-0 opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100 [&>svg]:size-3"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              size="sm"
              type="button"
              variant="standard"
            >
              <XIcon />
            </DsIconButton>
          )}
        </>
      )}
    </div>
  );
}

export type MessageAttachmentsProps = ComponentProps<'div'>;

export function MessageAttachments({
  children,
  className,
  ...props
}: MessageAttachmentsProps) {
  if (!children) {
    return null;
  }

  return (
    <div
      className={cn(
        'ml-auto flex w-fit flex-wrap items-start gap-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export type MessageToolbarProps = ComponentProps<'div'>;

export const MessageToolbar = ({
  className,
  children,
  ...props
}: MessageToolbarProps) => (
  <div
    className={cn(
      'mt-4 flex w-full items-center justify-between gap-4',
      className
    )}
    {...props}
  >
    {children}
  </div>
);
